import { Button, ButtonGroup } from "@msuite/ui/button";
import { type Disclosure, type UseLoading, useLoading } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Popover } from "@msuite/ui/popover";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import { HolidayTag } from "@msuite/ui/tag";
import { Tooltip } from "@msuite/ui/tooltip";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import { Holidays } from "@msuite/utils/holidays";
import { getPlanStateColor, getPlanStateString } from "@msuite/utils/plans";
import type { Nullable } from "@msuite/utils/types";
import { mutators, type ZeroPlan } from "@msuite/zero";
import { useZero } from "@msuite/zero/react";
import { syncHandler } from "@/api/sync-handler";
import { usePlanStore } from "@/components/plan/store";
import { AddCrewPopover } from "@/components/popovers/add-crew-popover";
import { DailyCopyDayPopover } from "@/components/popovers/daily-copy-day-popover";
import { client } from "@/hono";
import { useDailyContext } from "./context";

/** Props Interface */
interface DailyHeaderProps {
	plan: Nullable<ZeroPlan>;
	disclosure: Disclosure;
	hasCrews: boolean;
	copyLoader: UseLoading;
}

export function DailyHeader({ plan, disclosure, hasCrews, copyLoader }: DailyHeaderProps) {
	/** Context */
	const {
		date,
		isInteractionBlocked,
		canArrangeBoard,
		rulesState,
		showTicketsDisclosure,
		vehicleDisclosure,
	} = useDailyContext();

	/** Store */
	const activeDate = usePlanStore((state) => state.activeDate);
	const setActiveDate = usePlanStore((state) => state.setActiveDate);

	/** Hooks */
	const z = useZero();
	const { t } = useTranslation();
	const publishLoader = useLoading();

	/** Functions */
	async function handleOnPublishPlan() {
		try {
			publishLoader.start();
			if (!plan?.id) return;
			const response = await client.plan[":planId"].state.$patch({
				param: { planId: plan.id },
			});
			// The route gates on `update Plan` and the handler refuses a board that is not the
			// caller's, so this call has two ways to come back a refusal. Reading it through
			// `responseHandler` is what turns either one into the toast the rest of the app shows
			// instead of a button that appears to have worked.
			await responseHandler(response);
		} catch (error) {
			console.error("Failed to publish plan:", error);
		} finally {
			publishLoader.stop();
		}
	}

	function handleOnSetActive() {
		if (isActive) return;
		setActiveDate(date);
	}

	function handleOnToggleNotificationsEnabled() {
		if (!plan?.id) return;
		syncHandler(z.mutate(mutators.plans.toggleNotificationsEnabled({ planId: plan.id })));
	}

	/** Computed */
	const isActive = activeDate.db() === date;
	const holiday = Holidays.getHolidayByDate(moment(date).toDate(), "BE");
	// A day nobody has opened yet has no `plans` row, and every control below that names a plan id
	// needs one. The header itself does not: the date, the day switch and the two view toggles are
	// what a reader of the board uses, and a caller without `create Plan` never opens a day.
	const canPlan = !!plan?.id && canArrangeBoard;
	const canCopyDay = canPlan && !hasCrews && !isInteractionBlocked;

	/** Render */
	return (
		<div className="h-stack gap-0 justify-between items-center mb-2">
			<p className="font-medium">{moment(date).dateFormat()}</p>
			<div className="h-stack">
				<HolidayTag holiday={holiday} />
				<ButtonGroup isAttached={false}>
					{canCopyDay && plan && (
						<Popover
							trigger="click"
							render={() => (
								<DailyCopyDayPopover
									planId={plan.id}
									date={plan.date}
									copyLoader={copyLoader}
								/>
							)}
							placement="bottom"
						>
							<Tooltip label={t("planning.copy_day")}>
								<Button
									size="xxs"
									icon={<Icon name="Calendar" />}
								/>
							</Tooltip>
						</Popover>
					)}
					<Button
						size="xxs"
						icon={<StatusIndicator color={isActive ? "emerald" : "gray"} />}
						onClick={handleOnSetActive}
					>
						{t(isActive ? "planning.active" : "planning.inactive")}
					</Button>
					<Button
						size="xxs"
						icon={<Icon name="Truck" />}
						onClick={vehicleDisclosure.onToggle}
					>
						{t("vehicles.vehicles")}
					</Button>
					<Tooltip
						label={t(
							showTicketsDisclosure.isOpen ? "planning.hide_tickets" : "planning.show_tickets",
						)}
					>
						<Button
							size="xxs"
							icon={
								<Icon name={showTicketsDisclosure.isOpen ? "FoldVertical" : "UnfoldVertical"} />
							}
							onClick={showTicketsDisclosure.onToggle}
						/>
					</Tooltip>
					{canPlan && !isInteractionBlocked && plan && (
						<Popover
							trigger="click"
							render={() => <AddCrewPopover planId={plan.id} />}
						>
							<Button
								size="xxs"
								icon={<Icon name="ListPlus" />}
							>
								{t("planning.crews.add_crew")}
							</Button>
						</Popover>
					)}
				</ButtonGroup>
				<ButtonGroup isAttached={false}>
					{plan && (
						// The one control that stays on the header whatever the caller may do, so it is
						// where the three states of the rule list have to be told apart: a list still
						// in flight reads as loading rather than as a button disabled because the
						// answer was no, and a list that failed to load says so rather than looking
						// like the same refusal. All three leave the button unusable, which is right
						// for a control that writes — what differs is what the person is told.
						<Tooltip
							label={t("planning.rules_could_not_be_loaded")}
							isDisabled={rulesState !== "error"}
						>
							<Button
								size="xxs"
								icon={<StatusIndicator color={getPlanStateColor(plan)} />}
								onClick={handleOnPublishPlan}
								isLoading={publishLoader.isLoading || rulesState === "loading"}
								isDisabled={!canPlan}
							>
								{t(getPlanStateString(plan.state))}
							</Button>
						</Tooltip>
					)}
					{canPlan && plan && (
						<Tooltip label={t("planning.notify_about_changes")}>
							<Button
								size="xxs"
								icon={<Icon name={plan.notificationsEnabled ? "Bell" : "BellOff"} />}
								onClick={handleOnToggleNotificationsEnabled}
							/>
						</Tooltip>
					)}
					<Button
						size="xxs"
						icon={<Icon name="ChevronLeft" />}
						onClick={disclosure.onClose}
					/>
				</ButtonGroup>
			</div>
		</div>
	);
}
